/**
 * History Screen - Past analysis results (Simplified UX)
 *
 * Simplified to focus on core functionality:
 * - Clean list of past analyses with basic grouping
 * - Simple pull-to-refresh
 * - Modal detail view with re-analyze option
 * - Removed: infinite scroll, premium features, excessive analytics
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { View, Text, ScrollView, RefreshControl, TouchableOpacity, ActivityIndicator } from "react-native";
import { useRouter } from "expo-router";
import * as Sentry from "@sentry/react-native";
import { useTranslation, type DramaCategory } from "@dramadetector/shared";
import { useResponsiveScreenPadding } from "../../components/layout/TabScreenWrapper";
import { useInfiniteAnalysisHistory, useAnalysisStats, transformAnalysisRecord, type AnalysisRecord } from "@dramadetector/api-client";
import { TierButton, defaultTheme } from "@dramadetector/ui";
import { AnalysisDetailModal } from "../../components/AnalysisDetailModal";
import { useAuth } from "../../contexts/AuthContext";
import { GlassBackground, GlassPill, GlassCard } from "../../components/glass";
import { Logo } from "../../components/Logo";
import { ScreenSkeleton } from "../../components/skeleton/ScreenSkeleton";
import { captureEvent } from "../../lib/analytics";

// Simplified date grouping helper
const groupByDate = (analyses: AnalysisRecord[], todayLabel: string, yesterdayLabel: string) => {
  const groups = new Map<string, AnalysisRecord[]>();
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

  analyses.forEach((analysis) => {
    const date = new Date(analysis.createdAt);

    let dateKey: string;
    if (date.toDateString() === today.toDateString()) {
      dateKey = todayLabel;
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateKey = yesterdayLabel;
    } else {
      dateKey = date.toLocaleDateString();
    }

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(analysis);
  });

  return Array.from(groups.entries()).map(([dateKey, items]) => ({
    dateKey,
    items: items.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ),
  }));
};

// Category colors keyed by API raw key
const CATEGORY_COLORS: Record<DramaCategory | 'unknown', string> = {
  safe: '#dcfce7',
  caution: '#fef9c3',
  high_drama: '#fee2e2',
  extreme_drama: '#fce7f3',
  unknown: '#e5e7eb',
};

// Translate a raw API category key using the history_item.category namespace
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCategory = (t: any, key: string | undefined | null): string => {
  if (!key) return "Unknown";
  const translated = t(`history_item.category.${key}`);
  // Fall back to the raw key (capitalized) if translation is missing
  if (!translated || translated.startsWith('history_item.category.')) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return translated;
};

const AnalysisItem = ({
  analysis,
  onPress,
}: {
  analysis: AnalysisRecord;
  onPress: (analysis: AnalysisRecord) => void;
}) => {
  const { t } = useTranslation('history', { useSuspense: false });
  const categoryBg = CATEGORY_COLORS[analysis.category as DramaCategory | 'unknown'];
  const categoryLabel = translateCategory(t, analysis.category as DramaCategory | 'unknown');

  return (
    <TouchableOpacity onPress={() => onPress(analysis)} style={{ marginBottom: 12 }}>
      <GlassCard>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <View style={{ backgroundColor: categoryBg, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999 }}>
            <Text style={{ fontSize: 12, fontWeight: '500' }}>{categoryLabel}</Text>
          </View>
          <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#111827' }}>{analysis.score}</Text>
        </View>

        <Text style={{ color: '#1f2937', fontSize: 14, marginBottom: 8 }} numberOfLines={2}>
          {analysis.summary}
        </Text>

        <Text style={{ fontSize: 12, color: '#6b7280' }}>
          {new Date(analysis.createdAt).toLocaleTimeString()}
        </Text>
      </GlassCard>
    </TouchableOpacity>
  );
};

export default function HistoryScreen() {
  const router = useRouter();
  const { user, accessToken, loading: authLoading, isAnonymous } = useAuth();
  const { t } = useTranslation('history', { useSuspense: false });
  const { bottomPadding } = useResponsiveScreenPadding();

  const [selectedAnalysis, setSelectedAnalysis] = useState<AnalysisRecord | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Fetch analysis history with server-side pagination
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isRefetching } = useInfiniteAnalysisHistory({
    userId: user?.id,
    enabled: !!user && !authLoading && !!accessToken,
    pageSize: 15,
  });

  // Fetch stats for total count display
  const { data: stats } = useAnalysisStats({
    userId: user?.id,
    enabled: !!user && !authLoading && !!accessToken,
  });

  // Hook emits the validated wire DTO (snake_case); adapt each entry to the
  // camelCase `AnalysisRecord` shape the UI components expect. See PR #954.
  const allAnalyses = useMemo<AnalysisRecord[]>(
    () =>
      (data?.pages.flat() ?? [])
        .map((entry) => transformAnalysisRecord(entry as unknown as Record<string, unknown>))
        .filter((record): record is AnalysisRecord => record !== null),
    [data?.pages],
  );

  // Group analyses by date
  const groupedAnalyses = useMemo(() => {
    return groupByDate(
      allAnalyses,
      t('history_grouping.today' as never),
      t('history_grouping.yesterday' as never),
    );
  }, [allAnalyses, t]);

  const handleItemPress = useCallback((analysis: AnalysisRecord) => {
    setSelectedAnalysis(analysis);
    setModalVisible(true);
  }, []);

  const handleEditAndReanalyze = useCallback((message: string) => {
    setModalVisible(false);
    router.push({
      pathname: '/',
      params: { message },
    });
  }, [router]);

  // Report history fetch errors to Sentry so they're visible in the dashboard.
  // Without this, errors only show in the UI error state and are invisible to monitoring.
  useEffect(() => {
    if (error) {
      Sentry.captureMessage('History screen fetch failed', {
        level: 'error',
        extra: { code: (error as { code?: string }).code, message: error.message, userId: user?.id },
      });
    }
  }, [error, user?.id]);

  // Anon-engagement funnel: pairs with Anon_History_CtaClicked.
  useEffect(() => {
    if (isAnonymous && user?.id) {
      captureEvent('Anon_History_Viewed', { source: 'history' });
    }
  }, [isAnonymous, user?.id]);

  // Auth pending or data loading — show skeleton (not ActivityIndicator) per
  // ANON_AUTHED_INTEGRATION.md §PR checklist. Why: skeleton preserves layout
  // and reduces perceived latency during the ~200–500ms JWT fetch window.
  if (authLoading || isLoading) {
    return <ScreenSkeleton variant="list" testID="history-screen-skeleton" />;
  }

  // Error state
  if (error) {
    return (
      <GlassBackground>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <GlassCard style={{ maxWidth: 384 }}>
            <Text style={{ textAlign: 'center', color: '#dc2626', fontWeight: '600', marginBottom: 8 }}>
              {t('history_list.errors.load_failed' as never)}
            </Text>
            <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: 16 }}>
              {error.message}
            </Text>
            <TouchableOpacity
              onPress={() => refetch()}
              style={{ backgroundColor: '#3b82f6', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 }}
            >
              <Text style={{ color: 'white', textAlign: 'center', fontWeight: '500' }}>
                {t('history_list.errors.pull_to_retry' as never)}
              </Text>
            </TouchableOpacity>
          </GlassCard>
        </View>
      </GlassBackground>
    );
  }

  // Empty state — first-time view, no analyses yet.
  // Per ANON_AUTHED_INTEGRATION.md §"Canonical pattern: Screens" rule 3,
  // anon users get a "go analyze" CTA here (no sign-up nudge — there's no
  // value to "keep" yet, and asking to sign up before showing value is the
  // worst conversion shape per the spec §3.5).
  if (allAnalyses.length === 0) {
    return (
      <GlassBackground>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <GlassCard style={{ maxWidth: 384 }}>
            <Text style={{ textAlign: 'center', fontSize: 60, marginBottom: 16 }}>📊</Text>
            <Text style={{ textAlign: 'center', fontWeight: '600', color: '#111827', marginBottom: 8 }}>
              {t('history_list.empty.title' as never)}
            </Text>
            <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: isAnonymous ? 16 : 0 }}>
              {t('history_list.empty.message' as never)}
            </Text>
            {isAnonymous && (
              <View style={{ marginTop: 8 }}>
                <TierButton
                  variant="primary"
                  size="md"
                  fullWidth
                  onPress={() => {
                    captureEvent('Anon_History_CtaClicked', { source: 'history', cta: 'empty_analyze' });
                    router.push('/');
                  }}
                  testID="history-anon-empty-cta-analyze"
                >
                  {t('history.anon.empty.cta_analyze' as never)}
                </TierButton>
              </View>
            )}
          </GlassCard>
        </View>
      </GlassBackground>
    );
  }

  return (
    <GlassBackground>
      <View className="flex-1">
        {/* Header */}
        <View className="items-center px-4 pt-4 pb-2">
          <Logo size="lg" style={{ marginBottom: 16 }} />
          <GlassPill translationKey="tabs.history" size="lg" testID="history-header" />
        </View>

        {/* History List */}
        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingBottom: bottomPadding + 16,
            paddingTop: 16
          }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              tintColor={defaultTheme.brandPrimary}
              colors={[defaultTheme.brandPrimary]}
            />
          }
        >
          {/* Total count */}
          {stats?.totalAnalyses !== undefined && (
            <View className="mb-4">
              <Text className="text-center text-gray-600 text-sm">
                {stats.totalAnalyses} {t('history.subtitle' as never)}
              </Text>
            </View>
          )}

          {/* Grouped analyses */}
          {groupedAnalyses.map((group) => (
            <View key={group.dateKey} className="mb-6">
              <Text className="font-semibold text-gray-700 mb-3 px-2">
                {group.dateKey}
              </Text>
              {group.items.map((analysis) => (
                <AnalysisItem
                  key={analysis.id}
                  analysis={analysis}
                  onPress={handleItemPress}
                />
              ))}
            </View>
          ))}

          {/* Load More */}
          {hasNextPage && (
            <View style={{ marginBottom: 8 }}>
              <TierButton
                variant="primary"
                size="md"
                fullWidth
                loading={isFetchingNextPage}
                onPress={() => fetchNextPage()}
                testID="history-load-more"
              >
                {t('history_list.load_more' as never)}
              </TierButton>
            </View>
          )}

          {/* Anon retention CTA — shown ONLY when anon user has accumulated history.
              Per ANON_AUTHED_INTEGRATION.md "CTA pattern": one CTA per screen,
              retention-framed (not paywall), tagged with `?source=` for the
              PostHog funnel. Spec ref: docs/ux/anon-screens-spec-116.md §3.4. */}
          {isAnonymous && allAnalyses.length > 0 && (
            <GlassCard style={{ marginTop: 16, marginBottom: 8 }}>
              <Text style={{ textAlign: 'center', fontWeight: '600', color: '#111827', marginBottom: 8 }}>
                {t('history.anon.cta.headline' as never)}
              </Text>
              <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: 16 }}>
                {t('history.anon.cta.body' as never)}
              </Text>
              <TierButton
                variant="primary"
                size="md"
                fullWidth
                onPress={() => {
                  captureEvent('Anon_History_CtaClicked', { source: 'history', cta: 'retention' });
                  router.push('/(auth)/signup?source=anon_history');
                }}
                testID="history-anon-retention-cta"
              >
                {t('history.anon.cta.button' as never)}
              </TierButton>
            </GlassCard>
          )}
        </ScrollView>

        {/* Analysis Detail Modal */}
        <AnalysisDetailModal
          visible={modalVisible}
          analysis={selectedAnalysis}
          onClose={() => setModalVisible(false)}
          onEditAndReanalyze={handleEditAndReanalyze}
        />
      </View>
    </GlassBackground>
  );
}
